import React, { useState, useEffect } from 'react';
import { MapPin, Plus, Edit, Trash2, Users, CheckCircle, Clock, XCircle, AlertCircle, Save } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const TableManagement = () => {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  const [formData, setFormData] = useState({
    table_number: '',
    table_name: '',
    capacity: 4,
    floor_section: 'Main',
    status: 'available'
  });
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSection, setFilterSection] = useState('');

  const token = localStorage.getItem('token');
  const orgId = localStorage.getItem('current_organization_id');
  const locationId = localStorage.getItem('current_location_id');

  useEffect(() => {
    fetchTables();
  }, [filterStatus, filterSection]);

  const fetchTables = async () => {
    setLoading(true);
    setError(null);
    try {
      let url = `${API_URL}/api/pos/dinein/tables`;
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      if (filterSection) params.append('floor_section', filterSection);
      if (params.toString()) url += `?${params.toString()}`;

      const res = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Organization-Id': orgId || '',
          ...(locationId && { 'X-Location-Id': locationId })
        }
      });
      if (res.ok) {
        const data = await res.json();
        setTables(data);
      } else {
        setError('Failed to fetch tables');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingTable(null);
    setFormData({
      table_number: '',
      table_name: '',
      capacity: 4,
      floor_section: 'Main',
      status: 'available'
    });
    setShowModal(true);
  };

  const openEditModal = (table) => {
    setEditingTable(table);
    setFormData({
      table_number: table.table_number,
      table_name: table.table_name || '',
      capacity: table.capacity,
      floor_section: table.floor_section || 'Main',
      status: table.status
    });
    setShowModal(true);
  };

  const saveTable = async () => {
    setLoading(true);
    setError(null);
    try {
      const method = editingTable ? 'PUT' : 'POST';
      const url = editingTable
        ? `${API_URL}/api/pos/dinein/tables/${editingTable.id}`
        : `${API_URL}/api/pos/dinein/tables`;

      const payload = {
        ...formData,
        capacity: parseInt(formData.capacity)
      };

      const res = await fetch(url, {
        method: method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Organization-Id': orgId || '',
          ...(locationId && { 'X-Location-Id': locationId }),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        alert(`Table ${editingTable ? 'updated' : 'created'} successfully!`);
        setShowModal(false);
        fetchTables();
      } else {
        const errData = await res.json();
        setError(errData.detail || `Failed to ${editingTable ? 'update' : 'create'} table`);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateTableStatus = async (tableId, newStatus) => {
    try {
      const res = await fetch(`${API_URL}/api/pos/dinein/tables/${tableId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Organization-Id': orgId || '',
          ...(locationId && { 'X-Location-Id': locationId }),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (res.ok) {
        fetchTables();
      } else {
        const errData = await res.json();
        setError(errData.detail || 'Failed to update table status');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const deleteTable = async (tableId) => {
    if (!window.confirm('Are you sure you want to delete this table?')) return;

    setLoading(true);
    try {
      // Note: Delete endpoint would need to be added to backend
      // For now, we'll just mark as inactive
      const res = await fetch(`${API_URL}/api/pos/dinein/tables/${tableId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Organization-Id': orgId || ''
        }
      });

      if (res.ok) {
        alert('Table deleted successfully!');
        fetchTables();
      } else {
        const errData = await res.json();
        setError(errData.detail || 'Failed to delete table');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      available: 'bg-green-100 text-green-800',
      occupied: 'bg-blue-100 text-blue-800',
      reserved: 'bg-yellow-100 text-yellow-800',
      dirty: 'bg-orange-100 text-orange-800',
      out_of_service: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    if (status === 'available') return <CheckCircle className="w-4 h-4" />;
    if (status === 'occupied') return <Users className="w-4 h-4" />;
    if (status === 'reserved') return <Clock className="w-4 h-4" />;
    if (status === 'dirty') return <AlertCircle className="w-4 h-4" />;
    return <XCircle className="w-4 h-4" />;
  };

  const floorSections = [...new Set(tables.map(t => t.floor_section).filter(Boolean))];

  if (loading && tables.length === 0) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Table Management</h1>
          <p className="text-gray-600">Manage restaurant tables and floor plan</p>
        </div>
        <button
          onClick={openCreateModal}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
        >
          <Plus className="w-5 h-5" />
          <span>Add Table</span>
        </button>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="mb-6 flex space-x-4">
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg"
        >
          <option value="">All Status</option>
          <option value="available">Available</option>
          <option value="occupied">Occupied</option>
          <option value="reserved">Reserved</option>
          <option value="dirty">Dirty</option>
          <option value="out_of_service">Out of Service</option>
        </select>

        <select
          value={filterSection}
          onChange={(e) => setFilterSection(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg"
        >
          <option value="">All Sections</option>
          {floorSections.map(section => (
            <option key={section} value={section}>{section}</option>
          ))}
        </select>
      </div>

      {/* Tables Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {tables.map(table => (
          <div
            key={table.id}
            className={`bg-white border-2 rounded-lg p-4 shadow-sm hover:shadow-md transition-all ${
              table.status === 'available' ? 'border-green-200' :
              table.status === 'occupied' ? 'border-blue-200' :
              table.status === 'reserved' ? 'border-yellow-200' :
              table.status === 'dirty' ? 'border-orange-200' :
              'border-red-200'
            }`}
          >
            <div className="flex justify-between items-start mb-2">
              <div>
                <div className="font-bold text-lg">Table {table.table_number}</div>
                {table.table_name && (
                  <div className="text-sm text-gray-500">{table.table_name}</div>
                )}
              </div>
              <div className="flex space-x-1">
                <button
                  onClick={() => openEditModal(table)}
                  className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => deleteTable(table.id)}
                  className="p-1 text-red-600 hover:bg-red-50 rounded"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center space-x-2 text-sm">
                <Users className="w-4 h-4 text-gray-400" />
                <span>{table.capacity} seats</span>
              </div>

              {table.floor_section && (
                <div className="flex items-center space-x-2 text-sm text-gray-500">
                  <MapPin className="w-4 h-4" />
                  <span>{table.floor_section}</span>
                </div>
              )}

              <div className="mt-3">
                <select
                  value={table.status}
                  onChange={(e) => updateTableStatus(table.id, e.target.value)}
                  className={`w-full text-xs font-medium rounded-full px-2 py-1 border-0 ${getStatusColor(table.status)}`}
                >
                  <option value="available">Available</option>
                  <option value="occupied">Occupied</option>
                  <option value="reserved">Reserved</option>
                  <option value="dirty">Dirty</option>
                  <option value="out_of_service">Out of Service</option>
                </select>
              </div>
            </div>
          </div>
        ))}
      </div>

      {tables.length === 0 && !loading && (
        <div className="text-center py-12 text-gray-500">
          <MapPin className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-lg font-medium">No tables configured</p>
          <p className="text-sm">Start by adding your first table</p>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-2xl font-bold mb-4">
              {editingTable ? 'Edit Table' : 'Add New Table'}
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Table Number *</label>
                <input aria-label="Table Number"
                  type="text"
                  value={formData.table_number}
                  onChange={(e) => setFormData({ ...formData, table_number: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="e.g., 1, 2A, VIP-1"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Table Name</label>
                <input aria-label="Table Name"
                  type="text"
                  value={formData.table_name}
                  onChange={(e) => setFormData({ ...formData, table_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="e.g., Window Table 1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Capacity *</label>
                <input aria-label="Capacity"
                  type="number"
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) || 1 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  min="1"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Floor Section</label>
                <input aria-label="Floor Section"
                  type="text"
                  value={formData.floor_section}
                  onChange={(e) => setFormData({ ...formData, floor_section: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="e.g., Main, Patio, VIP"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select aria-label="Status"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="available">Available</option>
                  <option value="occupied">Occupied</option>
                  <option value="reserved">Reserved</option>
                  <option value="dirty">Dirty</option>
                  <option value="out_of_service">Out of Service</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={saveTable}
                disabled={loading || !formData.table_number}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
              >
                <Save className="w-4 h-4" />
                <span>{editingTable ? 'Update' : 'Create'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TableManagement;
